import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  MoreVert,
  ArrowForward,
} from '@mui/icons-material';
import { Client, ClientStatus } from '@/types';
import ClientCard from './ClientCard';

interface ClientKanbanProps {
  clients: Client[];
  onClientClick?: (client: Client) => void;
  onStatusChange?: (client: Client, newStatus: ClientStatus) => void;
}

const STAGES = [
  { id: ClientStatus.DISCOVERY, label: 'Discovery', color: '#667eea' },
  { id: ClientStatus.RESERVATION, label: 'Reservation', color: '#764ba2' },
  { id: ClientStatus.BANK_INSTRUCTION, label: 'Bank Instruction', color: '#C5A059' },
  { id: ClientStatus.COMPROMIS, label: 'Compromis', color: '#8A9A5B' },
  { id: ClientStatus.CLOSING, label: 'Closing', color: '#3C3E3F' },
];

const ClientKanban: React.FC<ClientKanbanProps> = ({ clients, onClientClick, onStatusChange }) => {
  const getClientsByStatus = (status: ClientStatus) => {
    return clients.filter((client) => client.status === status);
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        gap: 2, 
        overflowX: 'auto', 
        pb: 2,
        minHeight: 'calc(100vh - 250px)',
        alignItems: 'flex-start'
      }}
    >
      {STAGES.map((stage) => {
        const stageClients = getClientsByStatus(stage.id);
        
        return (
          <Box 
            key={stage.id} 
            sx={{ 
              minWidth: 320, 
              width: 320,
              flexShrink: 0,
              bgcolor: 'rgba(240, 237, 233, 0.5)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100%'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stage.color }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {stage.label}
                </Typography>
                <Typography variant="caption" sx={{ bgcolor: 'white', px: 1, borderRadius: 1, fontWeight: 600 }}>
                  {stageClients.length}
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>

            <Stack spacing={2} sx={{ overflowY: 'auto', flex: 1 }}>
              {stageClients.map((client) => (
                <Box key={client.id} sx={{ position: 'relative' }}>
                  <ClientCard
                    client={client}
                    onView={onClientClick}
                    compact={true}
                  />
                  {onStatusChange && stage.id !== ClientStatus.CLOSING && (
                    <Tooltip title="Move to next stage">
                      <IconButton 
                        size="small"
                        sx={{ 
                          position: 'absolute', 
                          right: 8, 
                          bottom: 8, 
                          bgcolor: 'white',
                          boxShadow: 1,
                          '&:hover': { bgcolor: 'primary.main', color: 'white' }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const currentIndex = STAGES.findIndex(s => s.id === stage.id);
                          if (currentIndex < STAGES.length - 1) {
                            onStatusChange(client, STAGES[currentIndex + 1].id as ClientStatus);
                          }
                        }}
                      >
                        <ArrowForward fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
              {stageClients.length === 0 && (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center', 
                    borderStyle: 'dashed',
                    bgcolor: 'transparent'
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    No clients in this stage
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default ClientKanban;